import { type FC, useEffect, type ReactNode } from 'react';
import { useRelayEnvironment, graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { trackEvent } from 'dibs-tracking';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { EthereumConnectionZipCodeForm } from 'dibs-crypto-utils/exports/EthereumConnectionZipCodeForm.ts';
import { type ZipCodeOutput } from 'dibs-crypto-utils/exports/EthereumConnectionZipCodeForm.ts';
import { useWeb3Connection } from 'dibs-crypto-utils/src/useWeb3Connection.tsx';
import { NftTermsOfUseLink } from 'dibs-crypto-utils/exports/NftTermsOfUseLink.ts';
import { ethereumPaymentSectionUrl } from 'dibs-crypto-utils/exports/constants.ts';
import { WithWalletConnectionEnsuredModalBase } from './WithWalletConnectionEnsuredModalBase.tsx';
import { NftFaqLink } from './NftFaqLink.tsx';
import { alreadyConnectedMessage } from '../EthereumConnectionAlreadyConnectedModal.tsx';
import { type ConnectWalletWithSigningContent_viewer$key } from './__generated__/ConnectWalletWithSigningContent_viewer.graphql.ts';

import styles from './styles/ConnectWalletWithSigningContent.scss';

type Props = {
    userId: string | null;
    trackingLabel: string;
    viewer: ConnectWalletWithSigningContent_viewer$key;
    onWalletPersisted: () => void;
};

export const ConnectWalletWithSigningContent: FC<Props> = ({
    userId,
    trackingLabel,
    viewer: viewerRef,
    onWalletPersisted,
}) => {
    const environment = useRelayEnvironment();
    const { connected, initiateConnection } = useWeb3Connection();

    useEffect(() => {
        trackEvent({
            event: 'e_ga_event',
            category: 'NFT',
            action: 'metamask connect entry',
            label: trackingLabel,
        });
    }, [trackingLabel]);

    const viewer = useFragment(
        graphql`
            fragment ConnectWalletWithSigningContent_viewer on Viewer {
                ...WithWalletConnectionEnsuredModalBase_viewer
                ...EthereumConnectionZipCodeForm_viewer
            }
        `,
        viewerRef
    );

    const connectToWallet = ({
        zipCode,
        isInternationalCustomer,
        walletType,
    }: ZipCodeOutput): Promise<void> =>
        initiateConnection({
            environment,
            userId: userId || '',
            zipCode,
            isInternationalCustomer,
            walletType,
            forAddressAndBidderRole: true,
            onSign: () => {
                trackEvent({
                    event: 'e_ga_event',
                    category: 'NFT',
                    action: 'metamask connect complete',
                    label: 'CONNECT_METAMASK_WITH_SIGNING',
                });
                onWalletPersisted();
            },
        });

    let errorMessage: ReactNode = null;
    if (connected.unknownError) {
        errorMessage = (
            <FormattedMessage
                id="nft.withWalletConnection.connectMetamaskWithSigning.unexpectedError"
                defaultMessage="Unexpected error occured. Try reconnecting Ethereum address in <a>payment settings</a>."
                values={{
                    a: chunk => (
                        <Link
                            dataTn="my-account-ethereum-link"
                            href={ethereumPaymentSectionUrl}
                            target="_blank"
                            className={styles.errorLink}
                        >
                            {chunk}
                        </Link>
                    ),
                }}
            />
        );
    } else if (connected.alreadyConnected) {
        errorMessage = alreadyConnectedMessage;
    }
    return (
        <WithWalletConnectionEnsuredModalBase
            showWalletButtons={false}
            viewer={viewer}
            title={
                <FormattedMessage
                    id="nft.withWalletConnection.connectMetamaskWithSigning"
                    defaultMessage="Connect Your Wallet"
                />
            }
            paragraphs={[
                <FormattedMessage
                    id="nft.withWalletConnection.connectMetamaskWithSigning.bodyCopy1"
                    defaultMessage="In order to proceed, you are required to connect your wallet. No other forms of payment, including credit cards, will be accepted. By connecting your wallet, you agree that you have read and accepted the {termsOfUse}."
                    values={{
                        termsOfUse: <NftTermsOfUseLink />,
                    }}
                />,
            ]}
            footer={<NftFaqLink />}
        >
            <div className={styles.infoSection}>
                <FormattedMessage
                    defaultMessage="<b>United States customers:</b>  before proceeding, please provide your zip code. 1stDibs uses this information only to comply with state sales tax requirements."
                    id="nft.withWalletConnection.connectMetamask.zipCodeEntry.usCustomerInfo"
                    values={{
                        b: chunks => <strong>{chunks}</strong>,
                    }}
                />
            </div>

            <EthereumConnectionZipCodeForm
                showWalletButtons
                viewer={viewer}
                onClick={zipCodeData => connectToWallet(zipCodeData)}
                errorMessage={errorMessage}
            />
        </WithWalletConnectionEnsuredModalBase>
    );
};
